/**
 * Pruning helpers for the PCR dashboard's dependent filters.
 *
 * The PCR filter card has a parent filter (Service Area) and two children whose option lists are
 * scoped to it (Foreman Area, Road asset). Narrowing the parent can strand a child selection that
 * the new option list no longer offers, which would silently filter on a deselected service area.
 *
 * PCR prunes rather than clears, matching All Tasks and Submissions: a child pick that is still
 * valid under the new parent selection survives, and only orphans are dropped. Because the child
 * option lists are fetched per service area (up to four parallel requests), pruning must be gated
 * on "the loaded lists describe the CURRENT parent selection" — not merely on "the lists are
 * non-empty". These helpers are pure so that gate is testable without mounting the page.
 */

/**
 * Stable, order-insensitive key for a multi-value filter.
 *
 * Used both as a React dependency (so a new array with identical contents does not refire an
 * effect) and as the identity of "which service area selection did these options load for".
 */
export function idsKey(ids: readonly string[] | undefined): string {
  if (!ids?.length) return '';
  return [...ids].sort().join('\0');
}

/**
 * Whether the child filters may be pruned right now.
 *
 * Returns false — meaning "leave the child selections alone" — in every ambiguous state:
 *
 * - **No parent selection.** An empty Service Area selection means "all service areas", i.e. no
 *   constraint, so nothing is orphaned. Pruning here would hit the `[].includes(x) === false` trap
 *   and wipe every child pick.
 * - **Options not loaded.** `loadedForKey === null` covers first mount and the window after a
 *   parent change while the requests are still in flight. Guarding on load state rather than on
 *   `options.length > 0` matters because a service area can legitimately offer zero roads: a
 *   non-empty check would refuse to prune exactly when pruning is required.
 * - **Stale response.** When `loadedForKey` does not match the current parent selection the loaded
 *   lists describe an older selection, so they must not drive pruning.
 */
export function canPrunePcrChildFilters(args: {
  /** Current Service Area selection (the parent filter). */
  serviceAreaIds: readonly string[];
  /** Parent selection the currently loaded option lists were fetched for; null = not loaded. */
  loadedForKey: string | null;
}): boolean {
  if (args.serviceAreaIds.length === 0) return false;
  if (args.loadedForKey === null) return false;
  return args.loadedForKey === idsKey(args.serviceAreaIds);
}

/**
 * Drops selected ids that `availableIds` does not offer.
 *
 * Returns the original `selected` array by reference when nothing is pruned. That identity
 * stability is load-bearing: the caller passes this to a `useState` setter, and React bails out of
 * the re-render when the setter returns the same reference, which is what stops the
 * prune-writes-state-retriggers-prune loop.
 */
export function prunePcrSelectedIds(
  selected: string[],
  availableIds: Iterable<string>,
): string[] {
  if (selected.length === 0) return selected;
  const available = new Set(availableIds);
  const next = selected.filter((id) => available.has(id));
  return next.length === selected.length ? selected : next;
}
